import { useState, useEffect, useCallback } from 'react';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Slider } from './ui/slider';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { ScrollArea } from './ui/scroll-area';
import { 
  Play, 
  Pause, 
  Eye, 
  EyeSlash,
  Heartbeat, 
  Lightning, 
  Thermometer,
  Wind,
  Drop,
  Warning,
  FirstAid,
  ArrowClockwise,
  Skull,
  CheckCircle,
  Sliders,
  Clock,
  User,
  CaretDown,
  CaretUp,
  Pulse
} from '@phosphor-icons/react';
import { simulationsAPI } from '../api';
import { toast } from 'sonner';

// Configuración de atajos clínicos
const CLINICAL_SHORTCUTS = [
  { id: 'hipotension', name: 'Hipotensión', icon: Drop, color: 'bg-blue-600', description: 'PA baja' },
  { id: 'hipertension', name: 'Hipertensión', icon: Drop, color: 'bg-red-600', description: 'PA alta' },
  { id: 'desaturacion', name: 'Desaturación', icon: Wind, color: 'bg-purple-600', description: 'SpO2 baja' },
  { id: 'taquicardia', name: 'Taquicardia', icon: Heartbeat, color: 'bg-orange-600', description: 'FC alta' },
  { id: 'bradicardia', name: 'Bradicardia', icon: Heartbeat, color: 'bg-cyan-600', description: 'FC baja' },
  { id: 'taquipnea', name: 'Taquipnea', icon: Wind, color: 'bg-amber-600', description: 'FR alta' },
  { id: 'bradipnea', name: 'Bradipnea', icon: Wind, color: 'bg-teal-600', description: 'FR baja' },
  { id: 'fiebre', name: 'Fiebre', icon: Thermometer, color: 'bg-rose-600', description: 'Temp alta' },
  { id: 'hipotermia', name: 'Hipotermia', icon: Thermometer, color: 'bg-sky-600', description: 'Temp baja' },
  { id: 'paro_cardiaco', name: 'Paro Cardíaco', icon: Skull, color: 'bg-red-700', description: 'PCR' },
  { id: 'shock', name: 'Shock', icon: Warning, color: 'bg-red-600', description: 'Shock' },
  { id: 'recuperacion', name: 'Recuperación', icon: FirstAid, color: 'bg-emerald-600', description: 'Mejoría' },
  { id: 'estabilizacion', name: 'Estabilización', icon: CheckCircle, color: 'bg-green-600', description: 'Estable' },
];

// Configuración de vitales con rangos
const VITALS_CONFIG = {
  fc: { label: 'FC', unit: 'lpm', min: 0, max: 250, step: 1, icon: Heartbeat, color: 'text-red-400' },
  spo2: { label: 'SpO2', unit: '%', min: 0, max: 100, step: 1, icon: Wind, color: 'text-blue-400' },
  fr: { label: 'FR', unit: 'rpm', min: 0, max: 60, step: 1, icon: Pulse, color: 'text-cyan-400' },
  pa_sistolica: { label: 'PAS', unit: 'mmHg', min: 0, max: 300, step: 1, icon: Drop, color: 'text-amber-400' },
  pa_diastolica: { label: 'PAD', unit: 'mmHg', min: 0, max: 200, step: 1, icon: Drop, color: 'text-amber-300' },
  temperatura: { label: 'Temp', unit: '°C', min: 30, max: 42, step: 0.1, icon: Thermometer, color: 'text-orange-400' },
};

const InstructorPanel = ({ 
  sessionId, 
  currentVitals = {}, 
  patientState = 'estable',
  elapsedTime = 0,
  onVitalsUpdate,
  isVisible = true,
  onToggleVisibility 
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [activeTab, setActiveTab] = useState('shortcuts');
  const [isLoading, setIsLoading] = useState(false);
  const [controlState, setControlState] = useState('running');
  const [interventionHistory, setInterventionHistory] = useState([]);
  
  // Estado para modo manual
  const [manualVitals, setManualVitals] = useState({
    fc: currentVitals.fc || 80,
    spo2: currentVitals.spo2 || 98,
    fr: currentVitals.fr || 16,
    pa_sistolica: currentVitals.pa_sistolica || 120,
    pa_diastolica: currentVitals.pa_diastolica || 80,
    temperatura: currentVitals.temperatura || 36.5,
  });
  
  const [selectedState, setSelectedState] = useState(patientState);
  const [interventionReason, setInterventionReason] = useState('');

  // Sincronizar vitales manuales con vitales actuales
  useEffect(() => {
    setManualVitals({
      fc: currentVitals.fc || 80,
      spo2: currentVitals.spo2 || 98,
      fr: currentVitals.fr || 16,
      pa_sistolica: currentVitals.pa_sistolica || 120,
      pa_diastolica: currentVitals.pa_diastolica || 80,
      temperatura: currentVitals.temperatura || 36.5,
    });
  }, [currentVitals]);

  // Cargar estado de control e historial
  useEffect(() => {
    if (sessionId) {
      loadControlStatus();
      loadInterventionHistory();
    }
  }, [sessionId]);

  const loadControlStatus = async () => {
    try {
      const response = await simulationsAPI.getInstructorStatus(sessionId);
      setControlState(response.data.control_state);
    } catch (error) {
      console.error('Error loading control status:', error);
    }
  };

  const loadInterventionHistory = async () => {
    try {
      const response = await simulationsAPI.getInstructorInterventions(sessionId);
      setInterventionHistory(response.data.interventions || []);
    } catch (error) {
      console.error('Error loading intervention history:', error);
    }
  };

  // Aplicar intervención
  const applyIntervention = useCallback(async (interventionType, additionalData = {}) => {
    if (!sessionId) return;
    
    setIsLoading(true);
    try {
      const response = await simulationsAPI.instructorIntervene(sessionId, {
        intervention_type: interventionType,
        time_elapsed_seconds: elapsedTime,
        reason: interventionReason || undefined,
        ...additionalData
      });
      
      if (response.data.success) {
        toast.success(response.data.message, {
          description: `Cambios aplicados: ${Object.keys(response.data.changes_applied).join(', ')}`,
          icon: '🎓'
        });
        
        // Notificar cambios al componente padre
        if (onVitalsUpdate && response.data.new_vitals) {
          onVitalsUpdate(response.data.new_vitals, response.data.new_patient_state);
        }
        
        // Actualizar estado de control
        if (response.data.control_state) {
          setControlState(response.data.control_state);
        }
        
        // Recargar historial
        loadInterventionHistory();
        
        // Limpiar razón
        setInterventionReason('');
      }
    } catch (error) {
      console.error('Error applying intervention:', error);
      toast.error('Error al aplicar intervención', {
        description: error.response?.data?.detail || 'Error desconocido'
      });
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, elapsedTime, interventionReason, onVitalsUpdate]);

  // Aplicar atajo clínico
  const applyShortcut = (shortcutId) => {
    applyIntervention('clinical_shortcut', { shortcut: shortcutId });
  };

  // Aplicar cambios manuales
  const applyManualChanges = () => {
    applyIntervention('manual_vitals', {
      ...manualVitals,
      patient_state: selectedState
    });
  };

  // Controles de simulación
  const togglePause = () => {
    const action = controlState === 'paused' ? 'resume_simulation' : 'pause_simulation';
    applyIntervention(action);
  };

  const toggleFreeze = () => {
    const action = controlState === 'monitor_frozen' ? 'unfreeze_monitor' : 'freeze_monitor';
    applyIntervention(action);
  };

  if (!isVisible) {
    return (
      <Button
        onClick={onToggleVisibility}
        className="fixed bottom-4 right-4 z-50 bg-indigo-600 hover:bg-indigo-700 shadow-lg"
        data-testid="show-instructor-panel-btn"
      >
        <Sliders size={20} className="mr-2" />
        Panel Instructor
      </Button>
    );
  }

  return (
    <div 
      className={cn(
        "fixed right-4 bottom-4 z-50 w-96 transition-all duration-300",
        isExpanded ? "h-auto max-h-[80vh]" : "h-auto"
      )}
      data-testid="instructor-panel"
    >
      <Card className="bg-slate-900/95 border-indigo-500/50 backdrop-blur-sm shadow-2xl">
        {/* Header */}
        <CardHeader className="py-3 px-4 border-b border-slate-700 cursor-pointer" onClick={() => setIsExpanded(!isExpanded)}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
                <Sliders size={18} className="text-white" />
              </div>
              <div>
                <CardTitle className="text-sm font-semibold text-white">Panel del Instructor</CardTitle>
                <p className="text-xs text-slate-400">Control en vivo</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className={cn(
                "text-xs",
                controlState === 'running' ? 'border-green-500 text-green-400' :
                controlState === 'paused' ? 'border-yellow-500 text-yellow-400' :
                'border-blue-500 text-blue-400'
              )}>
                {controlState === 'running' ? 'Activo' : 
                 controlState === 'paused' ? 'Pausado' : 'Congelado'}
              </Badge>
              {isExpanded ? <CaretUp size={18} className="text-slate-400" /> : <CaretDown size={18} className="text-slate-400" />}
            </div>
          </div>
        </CardHeader>

        {isExpanded && (
          <CardContent className="p-0">
            {/* Control Buttons */}
            <div className="flex gap-2 p-3 border-b border-slate-700">
              <Button
                size="sm"
                variant={controlState === 'paused' ? 'default' : 'outline'}
                className={cn(
                  "flex-1",
                  controlState === 'paused' ? 'bg-yellow-600 hover:bg-yellow-700' : ''
                )}
                onClick={togglePause}
                disabled={isLoading}
              >
                {controlState === 'paused' ? <Play size={16} className="mr-1" /> : <Pause size={16} className="mr-1" />}
                {controlState === 'paused' ? 'Reanudar' : 'Pausar'}
              </Button>
              <Button
                size="sm"
                variant={controlState === 'monitor_frozen' ? 'default' : 'outline'}
                className={cn(
                  "flex-1",
                  controlState === 'monitor_frozen' ? 'bg-blue-600 hover:bg-blue-700' : ''
                )}
                onClick={toggleFreeze}
                disabled={isLoading}
              >
                {controlState === 'monitor_frozen' ? <Eye size={16} className="mr-1" /> : <EyeSlash size={16} className="mr-1" />}
                {controlState === 'monitor_frozen' ? 'Mostrar' : 'Congelar'}
              </Button>
            </div>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="w-full grid grid-cols-3 bg-slate-800 rounded-none">
                <TabsTrigger value="shortcuts" className="text-xs">Atajos</TabsTrigger>
                <TabsTrigger value="manual" className="text-xs">Manual</TabsTrigger>
                <TabsTrigger value="history" className="text-xs">Historial</TabsTrigger>
              </TabsList>

              {/* Atajos Clínicos */}
              <TabsContent value="shortcuts" className="mt-0">
                <ScrollArea className="h-64 p-3">
                  <div className="grid grid-cols-2 gap-2">
                    {CLINICAL_SHORTCUTS.map((shortcut) => {
                      const Icon = shortcut.icon;
                      return (
                        <Button
                          key={shortcut.id}
                          size="sm"
                          variant="outline"
                          className={cn(
                            "h-auto py-2 px-3 flex flex-col items-start justify-start gap-1 border-slate-600 hover:border-slate-500",
                            "hover:bg-slate-800"
                          )}
                          onClick={() => applyShortcut(shortcut.id)}
                          disabled={isLoading}
                          data-testid={`shortcut-${shortcut.id}`}
                        >
                          <div className="flex items-center gap-2 w-full">
                            <div className={cn("w-6 h-6 rounded flex items-center justify-center", shortcut.color)}>
                              <Icon size={14} className="text-white" weight="bold" />
                            </div>
                            <span className="text-xs font-medium text-white">{shortcut.name}</span>
                          </div>
                          <span className="text-[10px] text-slate-400">{shortcut.description}</span>
                        </Button>
                      );
                    })}
                  </div>
                </ScrollArea>
              </TabsContent>

              {/* Modo Manual */}
              <TabsContent value="manual" className="mt-0">
                <ScrollArea className="h-64 p-3">
                  <div className="space-y-4">
                    {Object.entries(VITALS_CONFIG).map(([key, config]) => {
                      const Icon = config.icon;
                      return (
                        <div key={key} className="space-y-1">
                          <div className="flex items-center justify-between">
                            <Label className={cn("text-xs flex items-center gap-1", config.color)}>
                              <Icon size={14} weight="bold" />
                              {config.label}
                            </Label>
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                value={manualVitals[key]}
                                onChange={(e) => setManualVitals(prev => ({
                                  ...prev,
                                  [key]: parseFloat(e.target.value) || 0
                                }))}
                                className="w-20 h-7 text-xs text-center bg-slate-800 border-slate-600"
                                min={config.min}
                                max={config.max}
                                step={config.step}
                              />
                              <span className="text-[10px] text-slate-400 w-10">{config.unit}</span>
                            </div>
                          </div>
                          <Slider
                            value={[manualVitals[key]]}
                            min={config.min}
                            max={config.max}
                            step={config.step}
                            onValueChange={([value]) => setManualVitals(prev => ({
                              ...prev,
                              [key]: value
                            }))}
                            className="py-1"
                          />
                        </div>
                      );
                    })}

                    {/* Estado clínico */}
                    <div className="pt-2 border-t border-slate-700">
                      <Label className="text-xs text-slate-300 mb-2 block">Estado Clínico</Label>
                      <div className="grid grid-cols-4 gap-1">
                        {['estable', 'alerta', 'critico', 'paro'].map((state) => (
                          <Button
                            key={state}
                            size="sm"
                            variant={selectedState === state ? 'default' : 'outline'}
                            className={cn(
                              "text-[10px] h-7 capitalize",
                              selectedState === state && state === 'estable' && 'bg-green-600',
                              selectedState === state && state === 'alerta' && 'bg-yellow-600',
                              selectedState === state && state === 'critico' && 'bg-orange-600',
                              selectedState === state && state === 'paro' && 'bg-red-600'
                            )}
                            onClick={() => setSelectedState(state)}
                          >
                            {state}
                          </Button>
                        ))}
                      </div>
                    </div>

                    {/* Razón de intervención */}
                    <div>
                      <Label className="text-xs text-slate-300 mb-1 block">Razón (opcional)</Label>
                      <Input
                        value={interventionReason}
                        onChange={(e) => setInterventionReason(e.target.value)}
                        placeholder="Razón pedagógica..."
                        className="h-8 text-xs bg-slate-800 border-slate-600"
                      />
                    </div>

                    {/* Botón aplicar */}
                    <Button
                      className="w-full bg-indigo-600 hover:bg-indigo-700"
                      onClick={applyManualChanges}
                      disabled={isLoading}
                      data-testid="apply-manual-changes-btn"
                    >
                      {isLoading ? (
                        <ArrowClockwise size={16} className="mr-2 animate-spin" />
                      ) : (
                        <Lightning size={16} className="mr-2" />
                      )}
                      Aplicar Cambios
                    </Button>
                  </div>
                </ScrollArea>
              </TabsContent>

              {/* Historial */}
              <TabsContent value="history" className="mt-0">
                <ScrollArea className="h-64">
                  {interventionHistory.length === 0 ? (
                    <div className="p-4 text-center text-slate-400 text-sm">
                      No hay intervenciones registradas
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-700">
                      {interventionHistory.slice().reverse().map((intervention, idx) => (
                        <div key={intervention.id || idx} className="p-3 hover:bg-slate-800/50">
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <Clock size={12} className="text-slate-400" />
                              <span className="text-xs text-slate-400">
                                {Math.floor(intervention.elapsed_seconds / 60)}:{(intervention.elapsed_seconds % 60).toString().padStart(2, '0')}
                              </span>
                            </div>
                            <Badge variant="outline" className="text-[10px]">
                              {intervention.intervention_type}
                            </Badge>
                          </div>
                          <p className="text-xs text-white">
                            {intervention.shortcut ? 
                              CLINICAL_SHORTCUTS.find(s => s.id === intervention.shortcut)?.name || intervention.shortcut :
                              'Cambio manual'
                            }
                          </p>
                          {intervention.reason && (
                            <p className="text-[10px] text-slate-400 mt-1">{intervention.reason}</p>
                          )}
                          {intervention.changes_applied && Object.keys(intervention.changes_applied).length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {Object.entries(intervention.changes_applied).map(([key, change]) => (
                                <Badge key={key} variant="secondary" className="text-[9px] bg-slate-700">
                                  {key}: {change.from} → {change.to}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </TabsContent>
            </Tabs>

            {/* Footer */}
            <div className="p-2 border-t border-slate-700 flex justify-between items-center">
              <span className="text-[10px] text-slate-500">
                {interventionHistory.length} intervenciones
              </span>
              <Button
                size="sm"
                variant="ghost"
                className="h-6 text-xs text-slate-400 hover:text-white"
                onClick={onToggleVisibility}
              >
                Ocultar panel
              </Button>
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
};

export default InstructorPanel;
